import {
  LOG_OUT,
  DESTINATIONS_LOADING,
  DESTINATIONS_SUCCESS,
  DESTINATIONS_FAILED,
  DESTINATION_BY_ID_LOADING,
  DESTINATION_BY_ID_SUCCESS,
  DESTINATION_BY_ID_FAILED,
  CREATE_DESTINATION_LOADING,
  CREATE_DESTINATION_SUCCESS,
  CREATE_DESTINATION_FAILED,
  UPDATE_DESTINATION_LOADING,
  UPDATE_DESTINATION_SUCCESS,
  UPDATE_DESTINATION_FAILED,
  DELETE_DESTINATION_LOADING,
  DELETE_DESTINATION_SUCCESS,
  DELETE_DESTINATION_FAILED,
  DESTINATION_SELECTED,
  DESTINATION_UNSELECTED,
  CREATE_GUIDE_LOADING,
  CREATE_GUIDE_SUCCESS,
  CREATE_GUIDE_FAILED,
  UPDATE_GUIDE_LOADING,
  UPDATE_GUIDE_SUCCESS,
  UPDATE_GUIDE_FAILED,
  DELETE_GUIDE_LOADING,
  DELETE_GUIDE_SUCCESS,
  DELETE_GUIDE_FAILED,
  UPLOAD_IMAGE_LOADING,
  UPLOAD_IMAGE_SUCCESS,
  UPLOAD_IMAGE_FAILED,
  UPLOAD_VIDEO_LOADING,
  UPLOAD_VIDEO_SUCCESS,
  UPLOAD_VIDEO_FAILED,
  EXAMPLE_DESTINATION_LOADING,
  EXAMPLE_DESTINATION_SUCCESS,
  EXAMPLE_DESTINATION_FAILED,
  DOWNLOAD_PDF_LOADING,
  DOWNLOAD_PDF_SUCCESS,
  DOWNLOAD_PDF_FAILED
} from "../constants/actionTypes"

import type { Destination, Guide, ReducerAction } from "../constants/types"

type DownloadingPdfState = Record<string, { guidebook: boolean; brochure: boolean }>

type DestinationsState = {
  isLoading: boolean
  destinations: Destination[]
  selectedDestinationId: string | undefined
  example: Destination | undefined
  downloadingPdf: DownloadingPdfState
}

function replaceDestination(list: Destination[], next: Destination): Destination[] {
  return list.map(d => (d.id === next.id ? { ...d, ...next } : d))
}

function removeDestination(list: Destination[], id: string): Destination[] {
  return list.filter(d => d.id !== id)
}

function addGuideToDestination(list: Destination[], guide: Guide, destinationId?: string): Destination[] {
  if (!destinationId) return list
  const dst = list.find(d => d.id === destinationId)
  if (!dst) return list

  const guides = [...(dst.guides ?? []), guide]
  const updated = { ...dst, guides }

  return list.map(d => (d.id === updated.id ? updated : d))
}

function replaceGuideInDestination(list: Destination[], guide: Guide, destinationId?: string): Destination[] {
  if (!destinationId) return list
  const dst = list.find(d => d.id === destinationId)
  if (!dst) return list

  const guides = (dst.guides ?? []).map(g => (g.id === guide.id ? guide : g))
  const updated = { ...dst, guides }

  return list.map(d => (d.id === updated.id ? updated : d))
}

function removeGuideFromDestination(list: Destination[], guideId?: string, destinationId?: string): Destination[] {
  if (!destinationId || !guideId) return list
  const dst = list.find(d => d.id === destinationId)
  if (!dst) return list

  const guides = (dst.guides ?? []).filter(g => g.id !== guideId)
  const updated = { ...dst, guides }

  return list.map(d => (d.id === updated.id ? updated : d))
}

const initialState: DestinationsState = {
  isLoading: false,
  destinations: [],
  selectedDestinationId: undefined,
  example: undefined,
  downloadingPdf: {}
}

export default function (state: DestinationsState = initialState, action: ReducerAction): DestinationsState {
  switch (action.type) {
    // =============================================================================
    case DESTINATIONS_LOADING:
      return { ...state, isLoading: true }
    case DESTINATIONS_SUCCESS: {
      const payload = action.payload as unknown
      const destinations = Array.isArray(payload) ? (payload as Destination[]) : []

      return { ...state, isLoading: false, destinations }
    }
    case DESTINATIONS_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case DESTINATION_BY_ID_LOADING:
      return { ...state, isLoading: true }
    case DESTINATION_BY_ID_SUCCESS:
      return {
        ...state,
        isLoading: false,
        destinations: replaceDestination(state.destinations, action.payload as Destination)
      }
    case DESTINATION_BY_ID_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case DESTINATION_SELECTED:
      return { ...state, selectedDestinationId: action.payload as string }
    case DESTINATION_UNSELECTED:
      return { ...state, selectedDestinationId: undefined }

    // =============================================================================
    case CREATE_DESTINATION_LOADING:
      return { ...state, isLoading: true }
    case CREATE_DESTINATION_SUCCESS: {
      const destination = action.payload as Destination

      return {
        ...state,
        isLoading: false,
        destinations: [...state.destinations, destination],
        selectedDestinationId: destination.id
      }
    }
    case CREATE_DESTINATION_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case UPDATE_DESTINATION_LOADING:
      return { ...state, isLoading: true }
    case UPDATE_DESTINATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        destinations: replaceDestination(state.destinations, action.payload as Destination)
      }
    case UPDATE_DESTINATION_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case DELETE_DESTINATION_LOADING:
      return { ...state, isLoading: true }
    case DELETE_DESTINATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        destinations: removeDestination(state.destinations, action.payload as string)
      }
    case DELETE_DESTINATION_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case CREATE_GUIDE_LOADING:
      return { ...state, isLoading: true }
    case CREATE_GUIDE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        destinations: addGuideToDestination(state.destinations, action.payload as Guide, state.selectedDestinationId)
      }
    case CREATE_GUIDE_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case UPDATE_GUIDE_LOADING:
      return { ...state, isLoading: true }
    case UPDATE_GUIDE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        destinations: replaceGuideInDestination(
          state.destinations,
          action.payload as Guide,
          state.selectedDestinationId
        )
      }
    case UPDATE_GUIDE_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case DELETE_GUIDE_LOADING:
      return { ...state, isLoading: true }
    case DELETE_GUIDE_SUCCESS: {
      const meta = action.meta as { id?: string } | undefined
      const guideId = meta?.id

      return {
        ...state,
        isLoading: false,
        destinations: removeGuideFromDestination(state.destinations, guideId, state.selectedDestinationId)
      }
    }
    case DELETE_GUIDE_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case UPLOAD_IMAGE_LOADING:
      return { ...state, isLoading: true }
    case UPLOAD_IMAGE_SUCCESS:
      return { ...state, isLoading: false }
    case UPLOAD_IMAGE_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case UPLOAD_VIDEO_LOADING:
      return { ...state, isLoading: true }
    case UPLOAD_VIDEO_SUCCESS:
      return { ...state, isLoading: false }
    case UPLOAD_VIDEO_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case EXAMPLE_DESTINATION_LOADING:
      return { ...state, isLoading: true }
    case EXAMPLE_DESTINATION_SUCCESS:
      return { ...state, example: action.payload as Destination, isLoading: false }
    case EXAMPLE_DESTINATION_FAILED:
      return { ...state, isLoading: false }

    // =============================================================================
    case DOWNLOAD_PDF_LOADING: {
      const meta = action.meta as unknown as { destinationId: string; pdfType: "guidebook" | "brochure" }
      const currentState = state.downloadingPdf?.[meta.destinationId] ?? { guidebook: false, brochure: false }
      return {
        ...state,
        downloadingPdf: {
          ...state.downloadingPdf,
          [meta.destinationId]: {
            ...currentState,
            [meta.pdfType]: true
          }
        }
      }
    }
    case DOWNLOAD_PDF_SUCCESS:
    case DOWNLOAD_PDF_FAILED: {
      const meta = action.meta as unknown as { destinationId: string; pdfType: "guidebook" | "brochure" }
      const currentState = state.downloadingPdf?.[meta.destinationId] ?? { guidebook: false, brochure: false }
      return {
        ...state,
        downloadingPdf: {
          ...state.downloadingPdf,
          [meta.destinationId]: {
            ...currentState,
            [meta.pdfType]: false
          }
        }
      }
    }

    // =============================================================================
    case LOG_OUT:
      return initialState

    // =============================================================================
    default:
      break
  }
  return state
}
